import api from './api'

export interface LchWork {
  id: string
  name: string
  materialType: string
  densityLbPerCuFt: number
  maxTruckLoad: number
  defaultDumpCost: number
  defaultTruckingCostPerHr: number
  defaultLchTime: number
}

export interface LchTenantCost {
  workId: string
  name: string
  materialType: string
  densityLbPerCuFt: number
  maxTruckLoad: number
  dumpCost: number
  truckingCostPerHr: number
  lchTime: number
  hasOverride: boolean
  overrideId: string | null
}

export interface LchRow {
  id: string
  workId: string
  materialType: string
  section: string        // Which estimate tab owns this row (e.g., 'asphalt', 'concrete')
  name: string
  writeupRowId?: string // Links to writeup row ID (e.g., 'a-sr3') for auto-sync
  area: number
  depth: number
  // For curb/gutter/rolled-curb removal rows: length in linear feet × curbFactor (yd³/ft)
  // drives the tons calc instead of area × depth.
  length?: number
  curbFactor?: number
  // How this row prices TRUCKING:
  //   'clh'  — loads × CLH time × rate/hr. Every row saved before crew pricing
  //            existed, which is why a MISSING value means 'clh': those rows were
  //            bid on that formula and must never re-price themselves.
  //   'crew' — trucks × days × hours/day × rate/hr, for rows created since.
  // Dump cost is loads × cost per dump either way.
  pricing?: 'clh' | 'crew'
  lchTime: number          // 'clh' rows: haul hours per load
  trucks?: number          // 'crew' rows
  days?: number            // 'crew' rows — blank until the estimator fills it in
  hoursPerDay?: number     // 'crew' rows; defaults to DEFAULT_HOURS_PER_DAY
  truckingCostPerHr: number
  dumpCost: number
  maxTruckLoad: number
  densityLbPerCuFt: number
  // Computed (recalculated on the fly, but saved for PDF/reports)
  tons: number
  loads: number
  totalTruckingCost: number
  totalDumpCost: number
  totalHaulDumpCost: number
}

export const lchService = {
  // Global work templates
  async listWorks(): Promise<LchWork[]> {
    const { data } = await api.get('/lch/works')
    return data.data
  },

  // Tenant costs (merged with defaults)
  async listTenantCosts(): Promise<LchTenantCost[]> {
    const { data } = await api.get('/lch/costs')
    return data.data
  },

  // Upsert tenant cost override
  async upsertTenantCost(workId: string, costData: {
    dumpCost: number
    truckingCostPerHr: number
    maxTruckLoad: number
    lchTime: number
  }) {
    const { data } = await api.put(`/lch/costs/${workId}`, costData)
    return data.data
  },

  // Delete tenant cost override (revert to defaults)
  async deleteTenantCost(workId: string) {
    const { data } = await api.delete(`/lch/costs/${workId}`)
    return data.data
  },

  // Get estimate LCH calculator data
  async getEstimateLchData(estimateId: string): Promise<LchRow[] | null> {
    const { data } = await api.get(`/lch/estimates/${estimateId}`)
    return data.data
  },

  // Save estimate LCH calculator data
  async updateEstimateLchData(estimateId: string, lchData: LchRow[]) {
    const { data } = await api.put(`/lch/estimates/${estimateId}`, { lchData })
    return data.data
  },
}
